<template>
  <div class="filter-editor">
    <hr-filter-chip-group
      v-if="allowNegative"
      :model-value="modelValue.filterNegative"
      :options="negationOptions"
      @update:model-value="onNegationChange"
    />
    <!--
      Only for criteria whose value is a list, since an element with one value cannot be two
      things at once and "all" would match nothing.
    -->
    <hr-filter-chip-group
      v-if="supportsMatchMode(criterion)"
      :model-value="matchMode"
      :options="matchModeOptions"
      @update:model-value="emit('update:matchMode', $event)"
    />
    <hr-filter-chip-group
      :model-value="selection"
      :options="options"
      :multiple="!isBoolean"
      @update:model-value="onSelectionChange"
    />
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { filterPropertyTypes } from 'boot/globals';
import { computed } from 'vue';

import { HrFilterChipGroup } from 'src/design-system';

import { getBooleanOptions } from '../criteriaTypes';
import { MATCH_MODES, supportsMatchMode } from '../matchMode';

/**
 * Filters whose options are few enough to show at once, as a row of chips.
 *
 * Covers both a boolean, which picks one of three, and a short enumerated set, which picks any
 * number. The difference is only whether the chips are exclusive.
 */
defineOptions({ name: 'ChipsEditor' });

const props = defineProps({
  /** The criterion being edited. */
  criterion: {
    type: Object,
    required: true,
  },
  /** The statement being built for it. */
  modelValue: {
    type: Object,
    required: true,
  },
  /** Whether every chosen value must be present, or any one of them. */
  matchMode: {
    type: String,
    default: MATCH_MODES.ANY,
  },
});

const emit = defineEmits(['update:modelValue', 'update:matchMode']);

const isBoolean = computed(() => props.criterion.filterType === filterPropertyTypes.Boolean);

const options = computed(() => (isBoolean.value
  ? getBooleanOptions()
  : props.criterion.options?.filterCriteriaItems ?? []));

/** Criteria can offer an is / is not switch alongside the values. */
const allowNegative = computed(() => props.criterion.options?.allowNegative === true);

const negationOptions = computed(() => [
  { value: false, label: i18n.t('common.filter.filterPositive') },
  { value: true, label: i18n.t('common.filter.filterNegative') },
]);

const matchModeOptions = computed(() => [
  { value: MATCH_MODES.ANY, label: i18n.t('common.filter.matchAny') },
  { value: MATCH_MODES.ALL, label: i18n.t('common.filter.matchAll') },
]);

/*
 * A boolean's value is the choice itself, including null for "not selected", so it is passed
 * through untouched. Everything else collects an array, which starts as null on a fresh statement.
 */
const selection = computed(() => {
  if (isBoolean.value) {
    return props.modelValue.filteredItems;
  }
  return Array.isArray(props.modelValue.filteredItems) ? props.modelValue.filteredItems : [];
});

function patch(changes) {
  emit('update:modelValue', { ...props.modelValue, ...changes });
}

function onSelectionChange(value) {
  patch({ filteredItems: value });
}

function onNegationChange(value) {
  patch({ filterNegative: value === true });
}
</script>

<style lang="scss" scoped>
.filter-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
